import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Menu } from "lucide-react";
import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import logo from "@/assets/logo-sem-tagline.png";
import ThemeToggle from "./ThemeToggle";

const Header = () => {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const handleNavClick = (sectionId: string) => {
    setOpen(false);
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        element?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    } else {
      const element = document.getElementById(sectionId);
      element?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 backdrop-blur-md border-b border-[#E0D5C7]" style={{ backgroundColor: '#F3EDE3' }}>
      <div className="container mx-auto px-4 py-4">
        <div className="flex items-center justify-between">
          <button onClick={() => navigate('/')} className="flex items-center gap-2 cursor-pointer">
            <img src={logo} alt="EscriturIA" className="h-8 md:h-10 w-auto object-contain" />
          </button>
          
          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8">
            <button onClick={() => handleNavClick('recursos')} className="relative text-[#5D3A1A] hover:text-[#C4943B] transition-colors font-medium after:content-[''] after:absolute after:w-full after:scale-x-0 after:h-0.5 after:bottom-0 after:left-0 after:bg-[#C4943B] after:origin-bottom-right after:transition-transform after:duration-300 hover:after:scale-x-100 hover:after:origin-bottom-left">
              Recursos
            </button>
            <button onClick={() => handleNavClick('beneficios')} className="relative text-[#5D3A1A] hover:text-[#C4943B] transition-colors font-medium after:content-[''] after:absolute after:w-full after:scale-x-0 after:h-0.5 after:bottom-0 after:left-0 after:bg-[#C4943B] after:origin-bottom-right after:transition-transform after:duration-300 hover:after:scale-x-100 hover:after:origin-bottom-left">
              Benefícios
            </button>
            <button onClick={() => handleNavClick('planos')} className="relative text-[#5D3A1A] hover:text-[#C4943B] transition-colors font-medium after:content-[''] after:absolute after:w-full after:scale-x-0 after:h-0.5 after:bottom-0 after:left-0 after:bg-[#C4943B] after:origin-bottom-right after:transition-transform after:duration-300 hover:after:scale-x-100 hover:after:origin-bottom-left">
              Planos
            </button>
            <button onClick={() => handleNavClick('faq')} className="hidden lg:block relative text-[#5D3A1A] hover:text-[#C4943B] transition-colors font-medium after:content-[''] after:absolute after:w-full after:scale-x-0 after:h-0.5 after:bottom-0 after:left-0 after:bg-[#C4943B] after:origin-bottom-right after:transition-transform after:duration-300 hover:after:scale-x-100 hover:after:origin-bottom-left">
              FAQ
            </button>
          </nav>
          
          <div className="flex items-center gap-2">
            {/* Mobile Menu - moved to the left */}
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="lg" className="md:hidden text-[#5D3A1A] hover:text-[#C4943B] px-4 py-6 -mr-2">
                  <Menu className="h-9 w-9" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[280px] sm:w-[300px] bg-primary border-primary overflow-x-hidden">
                <nav className="flex flex-col gap-6 mt-8">
                  <button 
                    onClick={() => handleNavClick('recursos')}
                    className="text-lg text-primary-foreground hover:text-secondary transition-colors font-medium text-left"
                  >
                    Recursos
                  </button>
                  <button 
                    onClick={() => handleNavClick('beneficios')}
                    className="text-lg text-primary-foreground hover:text-secondary transition-colors font-medium text-left"
                  >
                    Benefícios
                  </button>
                  <button 
                    onClick={() => handleNavClick('planos')}
                    className="text-lg text-primary-foreground hover:text-secondary transition-colors font-medium text-left"
                  >
                    Planos
                  </button>
                  <button 
                    onClick={() => handleNavClick('faq')}
                    className="text-lg text-primary-foreground hover:text-secondary transition-colors font-medium text-left"
                  >
                    FAQ
                  </button>
                  <button 
                    onClick={() => { setOpen(false); navigate('/ajuda'); }}
                    className="text-lg text-primary-foreground hover:text-secondary transition-colors font-medium text-left"
                  >
                    Central de Ajuda
                  </button>
                  <button 
                    onClick={() => { setOpen(false); navigate('/sobre-nos'); }}
                    className="text-lg text-primary-foreground hover:text-secondary transition-colors font-medium text-left"
                  >
                    Sobre Nós
                  </button>
                  <a
                    href="https://escrituria.com.br/compromissos"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-lg text-primary-foreground hover:text-secondary transition-colors font-medium text-left"
                    onClick={() => setOpen(false)}
                  >
                    Compromissos
                  </a>
                  
                  <div className="flex flex-col gap-3 pt-6 border-t border-primary-foreground/20">
                    <div className="flex justify-start">
                      <ThemeToggle variant="mobile" />
                    </div>
                    <Button 
                      variant="ghost" 
                      size="lg" 
                      className="w-full justify-start text-primary-foreground hover:text-secondary"
                      onClick={() => { setOpen(false); navigate('/login'); }}
                    >
                      Login
                    </Button>
                    <Button 
                      variant="hero" 
                      size="lg" 
                      className="w-full"
                      onClick={() => { setOpen(false); navigate('/register'); }}
                    >
                      Registrar-se
                    </Button>
                  </div>
                </nav>
              </SheetContent>
            </Sheet>
            
            <div className="hidden md:flex items-center gap-4">
              <ThemeToggle />
              <Button 
                variant="ghost" 
                size="default" 
                className="text-[#5D3A1A] hover:text-[#C4943B]"
                onClick={() => navigate('/login')}
              >
                Login
              </Button>
              <Button 
                variant="hero" 
                size="default"
                onClick={() => navigate('/register')}
              >
                Registrar-se
              </Button>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
